import { useEffect } from "react";
import { useTranslation } from "react-i18next";
import { useForm } from "react-hook-form";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { zodResolver } from "@hookform/resolvers/zod";
import {
  latitudeSchema,
  longitudeSchema,
  type Site,
  type UpdateSiteRequest,
} from "@/contracts";
import { z } from "zod";

import { updateSiteMutation } from "@/api/queries";
import { Alert } from "@/components/ui/alert";
import { TextField } from "@/components/ui/field";
import { FormDialog } from "@/components/ui/form-dialog";
import { useApiClient } from "@/lib/api-context";
import { ApiError } from "@/lib/api-error";

/**
 * Coordinates arrive as text, because an empty number input yields `""` and
 * `Number("")` is 0 — which would drop a site in the Gulf of Guinea rather
 * than leaving it unplaced.
 */
const optionalCoordinate = z
  .string()
  .trim()
  .refine((value) => value === "" || Number.isFinite(Number(value)), {
    message: "Enter a number, or leave it empty.",
  });

/** Kept separate from the refinements below, which would hide `.shape`. */
const siteEditFields = z.object({
  name: z.string().trim().min(2, "Enter a name of at least 2 characters.").max(120),
  address: z.string().trim().max(240),
  latitude: optionalCoordinate,
  longitude: optionalCoordinate,
});

const siteEditSchema = siteEditFields
  .refine((value) => (value.latitude === "") === (value.longitude === ""), {
    message: "Give both coordinates, or neither.",
    path: ["longitude"],
  })
  .refine(
    (value) => value.latitude === "" || latitudeSchema.safeParse(Number(value.latitude)).success,
    { message: "Latitude runs from -90 to 90.", path: ["latitude"] },
  )
  .refine(
    (value) => value.longitude === "" || longitudeSchema.safeParse(Number(value.longitude)).success,
    { message: "Longitude runs from -180 to 180.", path: ["longitude"] },
  );

type SiteEditValues = z.infer<typeof siteEditSchema>;

function valuesFromSite(site: Site): SiteEditValues {
  return {
    name: site.name,
    address: site.address ?? "",
    latitude: site.latitude === null ? "" : String(site.latitude),
    longitude: site.longitude === null ? "" : String(site.longitude),
  };
}

export function SiteEditDialog({
  open,
  site,
  onClose,
}: {
  open: boolean;
  site: Site;
  onClose: () => void;
}) {
  const client = useApiClient();
  const { t } = useTranslation("pages");
  const { t: tCommon } = useTranslation("common");
  const queryClient = useQueryClient();

  const {
    register,
    handleSubmit,
    reset,
    setError,
    formState: { errors, isSubmitting },
  } = useForm<SiteEditValues>({
    resolver: zodResolver(siteEditSchema),
    defaultValues: valuesFromSite(site),
  });

  useEffect(() => {
    if (!open) {
      return;
    }

    reset(valuesFromSite(site));
  }, [open, site, reset]);

  const mutation = useMutation({
    ...updateSiteMutation(client),
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: ["sites"] });
    },
  });

  function close() {
    reset(valuesFromSite(site));
    mutation.reset();
    onClose();
  }

  const onSubmit = handleSubmit(async (values) => {
    const body: UpdateSiteRequest = {
      name: values.name,
      address: values.address === "" ? null : values.address,
      latitude: values.latitude === "" ? null : Number(values.latitude),
      longitude: values.longitude === "" ? null : Number(values.longitude),
    };

    try {
      await mutation.mutateAsync({ id: site.id, body });
      close();
    } catch (error) {
      if (error instanceof ApiError) {
        for (const [field, message] of Object.entries(error.fieldErrors())) {
          if (field in siteEditFields.shape) {
            setError(field as keyof SiteEditValues, { message });
          }
        }
      }
    }
  });

  return (
    <FormDialog
      open={open}
      title={t("dialogEditSiteTitle")}
      description={t("dialogEditSiteDescription")}
      submitLabel={tCommon("save")}
      pending={isSubmitting || mutation.isPending}
      onSubmit={(event) => void onSubmit(event)}
      onCancel={close}
    >
      <div className="flex flex-col gap-4">
        {mutation.isError ? <Alert>{mutation.error.message}</Alert> : null}

        <TextField
          label={tCommon("name")}
          required
          placeholder={t("placeholderSiteName")}
          {...(errors.name?.message === undefined ? {} : { error: errors.name.message })}
          {...register("name")}
        />

        <TextField
          label={tCommon("address")}
          hint={t("optionalHint")}
          {...(errors.address?.message === undefined ? {} : { error: errors.address.message })}
          {...register("address")}
        />

        <div className="grid gap-4 sm:grid-cols-2">
          <TextField
            label={t("fieldLatitude")}
            inputMode="decimal"
            placeholder="-31.4283"
            hint={t("latitudeHint")}
            {...(errors.latitude?.message === undefined ? {} : { error: errors.latitude.message })}
            {...register("latitude")}
          />

          <TextField
            label={t("fieldLongitude")}
            inputMode="decimal"
            placeholder="-64.1888"
            {...(errors.longitude?.message === undefined
              ? {}
              : { error: errors.longitude.message })}
            {...register("longitude")}
          />
        </div>
      </div>
    </FormDialog>
  );
}
